import React, { useState } from "react";
import DetachedModal from "../../../../Shared/Modals/DetachedModal/DetachedModal";
import { Input } from "../../../../ui/input";
import { BookingV4 } from "../../../../../types/bookings";
import { Checkbox } from "../../../../ui/checkbox";
import { Label } from "../../../../ui/label";
import { EDSButton } from "../../../../eds/EDSButton";
import { EDSDatePicker } from "../../../../eds/EDSDatePicker";
import { Button } from "../../../../Shared/Buttons/Button/Button";
import {
  ViewDepositTypeEnum,
  PAYMENT_TYPE_OPTIONS,
  OTHER_PAYMENT_TYPES
} from "../../../../../types/paymentTypes";
import { getCurrencySymbol } from "../../../../../utils/number";

type AddPaymentModalProps = {
  booking: BookingV4;
  onClose: () => void;
  onSubmit: (paymentData: PaymentFormData) => Promise<void>;
  initialAmount?: number;
};

export type PaymentFormData = {
  amount: number;
  type: ViewDepositTypeEnum | "paid";
  notes?: string;
  customerNote?: string;
  paidType?: string;
  date?: Date;
  emailReceipt?: boolean;
};

const AddPaymentModal = ({
  booking,
  onClose,
  onSubmit,
  initialAmount
}: AddPaymentModalProps) => {
  const [formData, setFormData] = useState<PaymentFormData>({
    amount: initialAmount ?? 0,
    type: ViewDepositTypeEnum.RequestPayment,
    notes: "",
    customerNote: "",
    paidType: "",
    date: new Date(),
    emailReceipt: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const currency = booking.deposits?.[0]?.currency || "GBP";

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = "Amount must be greater than 0";
    }

    if (formData.type === "paid" && !formData.paidType) {
      newErrors.paidType = "Payment type is required for Other Payment";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error("Error adding payment:", error);
      setErrors({ submit: "Failed to add payment. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof PaymentFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const isRequestType =
    formData.type === ViewDepositTypeEnum.RequestAuth ||
    formData.type === ViewDepositTypeEnum.RequestPayment;
  const isOtherPayment = formData.type === "paid";

  return (
    <DetachedModal heading="Add Payment" width="500px" handleClose={onClose}>
      <div className="AddPaymentModal flex flex-col h-full gap-4">
        <div className="AddPaymentModal-Form flex flex-col gap-4 flex-1 overflow-y-auto pr-1">
          <div className="AddPaymentModal-Field flex flex-col gap-2">
            <label
              htmlFor="payment-type"
              className="font-medium text-[color:var(--textColor)] text-sm"
            >
              Payment Type *
            </label>
            <div className="AddPaymentModal-ButtonGroup grid grid-cols-2 gap-2 mt-2">
              {PAYMENT_TYPE_OPTIONS.map((option) => (
                <EDSButton
                  key={option.value}
                  variant={
                    formData.type === option.value ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => handleInputChange("type", option.value)}
                  className="justify-start"
                >
                  {option.text}
                </EDSButton>
              ))}
            </div>
            {errors.type && (
              <span className="AddPaymentModal-Error text-[color:var(--errorColor,#d32f2f)] text-xs mt-1">
                {errors.type}
              </span>
            )}
          </div>

          <div className="AddPaymentModal-Field flex flex-col gap-2">
            <label
              htmlFor="payment-amount"
              className="font-medium text-[color:var(--textColor)] text-sm"
            >
              Amount *
            </label>
            <div className="AddPaymentModal-AmountContainer flex items-center gap-2 relative">
              <span className="AddPaymentModal-CurrencyPrefix font-medium text-[color:var(--textColor)] text-base shrink-0">
                {getCurrencySymbol(currency)}
              </span>
              <Input
                id="payment-amount"
                type="number"
                value={formData.amount.toString()}
                onChange={(e) =>
                  handleInputChange("amount", parseFloat(e.target.value) || 0)
                }
                placeholder="0.00"
                step="0.01"
                min="0"
                maxLength={15}
              />
            </div>
            {errors.amount && (
              <span className="AddPaymentModal-Error text-[color:var(--errorColor,#d32f2f)] text-xs mt-1">
                {errors.amount}
              </span>
            )}
          </div>

          {isOtherPayment && (
            <div className="AddPaymentModal-Field flex flex-col gap-2">
              <label
                htmlFor="paid-type"
                className="font-medium text-[color:var(--textColor)] text-sm"
              >
                Payment Method *
              </label>
              <div className="AddPaymentModal-ButtonGroup grid grid-cols-2 gap-2 mt-2">
                {OTHER_PAYMENT_TYPES.map((option) => (
                  <EDSButton
                    key={option.value}
                    variant={
                      formData.paidType === option.value ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => handleInputChange("paidType", option.value)}
                    className="justify-start"
                  >
                    {option.text}
                  </EDSButton>
                ))}
              </div>
              {errors.paidType && (
                <span className="AddPaymentModal-Error text-[color:var(--errorColor,#d32f2f)] text-xs mt-1">
                  {errors.paidType}
                </span>
              )}
            </div>
          )}

          {isOtherPayment && (
            <div className="AddPaymentModal-Field flex flex-col gap-2">
              <label
                htmlFor="payment-date"
                className="font-medium text-[color:var(--textColor)] text-sm"
              >
                Payment Date
              </label>
              <EDSDatePicker
                id="payment-date"
                date={formData.date}
                onSelect={(selectedDate) =>
                  handleInputChange("date", selectedDate || new Date())
                }
                placeholder="Select payment date"
              />
            </div>
          )}

          <div className="AddPaymentModal-Field flex flex-col gap-2">
            <label
              htmlFor="payment-notes"
              className="font-medium text-[color:var(--textColor)] text-sm"
            >
              Internal Notes
            </label>
            <textarea
              id="payment-notes"
              value={formData.notes}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              placeholder="Add internal notes about this payment..."
              rows={3}
              maxLength={900}
            />
          </div>

          {isRequestType && (
            <div className="AddPaymentModal-Field flex flex-col gap-2">
              <label
                htmlFor="customer-note"
                className="font-medium text-[color:var(--textColor)] text-sm"
              >
                Customer Message
              </label>
              <textarea
                id="customer-note"
                value={formData.customerNote}
                onChange={(e) =>
                  handleInputChange("customerNote", e.target.value)
                }
                placeholder="Message to include in customer email..."
                rows={3}
                maxLength={900}
              />
            </div>
          )}

          {formData.type === "paid" && (
            <div className="AddPaymentModal-Field flex flex-col gap-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="email-receipt"
                  checked={formData.emailReceipt || false}
                  onCheckedChange={(checked) =>
                    handleInputChange("emailReceipt", !!checked)
                  }
                />
                <Label htmlFor="email-receipt">
                  Send email receipt to customer
                </Label>
              </div>
            </div>
          )}

          {errors.submit && (
            <div className="AddPaymentModal-Error AddPaymentModal-SubmitError bg-[var(--errorBackgroundColor,#ffebee)] border border-[var(--errorColor,#d32f2f)] rounded p-3 mt-2 text-[color:var(--errorColor,#d32f2f)] text-xs">
              {errors.submit}
            </div>
          )}
        </div>

        <div className="AddPaymentModal-Actions mt-auto pt-4 shrink-0 flex gap-3 justify-end border-t border-[var(--borderColor)]">
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            fill="outline"
            size="md"
            type="default"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            fill="solid"
            size="md"
            type="primary"
          >
            {isSubmitting ? "Adding..." : "Add Payment"}
          </Button>
        </div>
      </div>
    </DetachedModal>
  );
};

export default AddPaymentModal;
